import { isEmpty, isNumber } from 'lodash';

import { FinancialSettlementAdvancePayment } from '@dreryk/egabinet-financial-settlement-frontend-api';

import { moneySubtract } from '@dreryk/react-common';

import { VisitSettlementBean } from '../../visitSettlement.types';
import { getMedicalServicePrices } from '../medicalServices/medicalServices.utils';
import { practitionerPriceDifferentFromServicePrice } from '../price/price.utils';
import { calculateTotalPrice } from '../totalPrice/totalPrice.utils';

import { AdjustmentFormBean, PreviousAdjustmentFormBean } from './adjustment/adjustment.types';
import { PaymentFormBean } from './payment/payment.types';
import { PractitionerFormBeanPart, VisitDetailsFormBean } from './visitDetails/visitDetails.types';
import { calculateAdvancePaymentsAmount } from './advancePayment/advancePayment.utils';

const editable = (visitSettlement: VisitSettlementBean): boolean => {
   return visitSettlement.editable;
};

const ownedByCurrentUser = (visitSettlement: VisitSettlementBean): boolean => {
   return visitSettlement.ownedByCurrentUser;
};

export const showMedicalServicesTable = (visitSettlement: VisitSettlementBean): boolean => {
   return editable(visitSettlement);
};

export const showMedicalServicesList = (visitSettlement: VisitSettlementBean): boolean => {
   return !editable(visitSettlement) && !isEmpty(visitSettlement.medicalServices);
};

export const medicalServiceSectionVisible = (visitSettlement: VisitSettlementBean): boolean => {
   return showMedicalServicesList(visitSettlement) || showMedicalServicesTable(visitSettlement);
};

const adjustmentAvailable = (adjustment: AdjustmentFormBean | undefined): adjustment is AdjustmentFormBean => {
   return Boolean(adjustment);
};

export const showPreviousAdjustmentNote = (
   adjustment: AdjustmentFormBean | undefined
): adjustment is AdjustmentFormBean & { previousAdjustment: PreviousAdjustmentFormBean } => {
   return Boolean(adjustmentAvailable(adjustment) && adjustment.previousAdjustment);
};

export const showAdjustmentNote = (adjustment: AdjustmentFormBean | undefined): boolean => {
   return Boolean(adjustmentAvailable(adjustment));
};

export const showPriceChangeReasonField = (
   visitSettlement: VisitSettlementBean,
   adjustment: AdjustmentFormBean | undefined
): boolean => {
   return editable(visitSettlement) && ownedByCurrentUser(visitSettlement) && !adjustmentAvailable(adjustment);
};

export const showPriceChangeReasonText = (
   visitSettlement: VisitSettlementBean,
   adjustment: AdjustmentFormBean | undefined
): visitSettlement is VisitSettlementBean & { priceChangeReason: string } => {
   if (showPriceChangeReasonField(visitSettlement, adjustment)) {
      return false;
   }

   return Boolean(
      visitSettlement.priceChangeReason || practitionerPriceDifferentFromServicePrice(visitSettlement.medicalServices)
   );
};

export const showPaymentEnabledToggle = (payment: PaymentFormBean): boolean => {
   return Boolean(!payment.required);
};

export const showPaymentFormPart = (payment: PaymentFormBean): boolean => {
   return Boolean(payment.enabled);
};

export const showPaymentMethodDescriptionInput = (payment: PaymentFormBean): boolean => {
   return payment.method === 'other';
};

export const areAdvancePaymentsAvailable = (
   advancePayments: FinancialSettlementAdvancePayment[] | undefined
): advancePayments is FinancialSettlementAdvancePayment[] => {
   if (!Array.isArray(advancePayments)) {
      return false;
   }

   const filteredAdvancePayments = advancePayments.filter(
      (advancePayment) => advancePayment.remainingAmount.asNumber() !== 0
   );

   return Boolean(filteredAdvancePayments.length);
};

export const showAdvancePaymentSection = (visitSettlement: VisitSettlementBean): boolean => {
   return (
      !editable(visitSettlement) &&
      isEmpty(visitSettlement.medicalServices) &&
      areAdvancePaymentsAvailable(visitSettlement.advancePayments)
   );
};

export const showPaymentCorrectlyCheckbox = (
   payment: PaymentFormBean,
   visitSettlement: VisitSettlementBean,
   adjustment: AdjustmentFormBean | undefined
): boolean => {
   if (!adjustmentAvailable(adjustment) || !isNumber(payment.amount) || isEmpty(visitSettlement.medicalServices)) {
      return false;
   }

   const calculatedAdvancePaymentAmount = calculateAdvancePaymentsAmount(visitSettlement.advancePayments);
   const calculatedTotalPrice = calculateTotalPrice(...getMedicalServicePrices(visitSettlement.medicalServices));
   const totalPrice = moneySubtract(calculatedTotalPrice, calculatedAdvancePaymentAmount);

   return payment.amount !== totalPrice;
};

export const showVisitDetailsFormPart = (
   visitDetails: VisitDetailsFormBean | undefined
): visitDetails is VisitDetailsFormBean => {
   return Boolean(visitDetails);
};

export const showPractitionerTextView = (
   visitDetails: VisitDetailsFormBean | undefined,
   adjustment: AdjustmentFormBean | undefined
): visitDetails is VisitDetailsFormBean & { practitioner: PractitionerFormBeanPart } => {
   return showVisitDetailsFormPart(visitDetails) && adjustmentAvailable(adjustment);
};
